import React, { useCallback, useContext, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { ThemeProvider } from 'styled-components';

import { ThemeContext, ThemeController } from 'styles';

import { Button, ButtonProps } from './button';
import { VerticalLayout } from './Layout';
import { ButtonsContainer, Modal, ModalContent } from './Modal';

interface Props {
    modalTitle: string;
    cancelButtonText?: string;
    onConfirm?: () => Promise<unknown> | void;
    buttonProps: Omit<ButtonProps, 'mutation' | 'mutationVar'>;
    onSubmit?: any;
    buttonDisabled?: boolean;
}

interface ModalProps extends Props {
    onClose: () => void;
    onSuccess?: () => void;
}

export const ConfirmModal = ({
    modalTitle,
    cancelButtonText,
    onClose,
    onConfirm,
    buttonDisabled,
    buttonProps,
    children,
}: PropsWithChildren<ModalProps>) => {
    const theme = useContext(ThemeContext);
    const [actionIsRunning, setActionIsRunning] = useState(false);

    const onConfirmClick = useCallback(async () => {
        setActionIsRunning(true);
        try {
            if (onConfirm) {
                await onConfirm();
                setActionIsRunning(false);
                onClose();
            }
        } catch (error) {
            setActionIsRunning(false);
        }
    }, [onClose, onConfirm]);

    // We render the modal into the dom again in the App.tsx when user leaves, thus we need the theme context here too.
    return (
        <ThemeController>
            <ThemeProvider theme={theme.light}>
                <Modal onClose={onClose} modalTitle={modalTitle}>
                    <ModalContent>
                        <VerticalLayout>{children}</VerticalLayout>
                    </ModalContent>
                    <ButtonsContainer $gap={16}>
                        <Button buttonType="secondary" onClick={onClose} disabled={buttonDisabled}>
                            {cancelButtonText ?? 'Cancel'}
                        </Button>
                        <Button
                            type="button"
                            {...buttonProps}
                            onClick={onConfirmClick}
                            waiting={actionIsRunning || buttonDisabled}
                        />
                    </ButtonsContainer>
                </Modal>
            </ThemeProvider>
        </ThemeController>
    );
};

export const ButtonWithConfirmModal = ({
    buttonProps,
    cancelButtonText,
    children,
    modalTitle,
    onConfirm,
}: PropsWithChildren<Props>) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);
    const openModal = useCallback(() => {
        setIsModalOpen(true);
    }, []);

    return (
        <>
            <Button type="button" {...buttonProps} onClick={openModal} />
            {isModalOpen ? (
                <ConfirmModal
                    modalTitle={modalTitle}
                    cancelButtonText={cancelButtonText}
                    onConfirm={onConfirm}
                    buttonProps={buttonProps}
                    onClose={closeModal}
                >
                    {children}
                </ConfirmModal>
            ) : null}
        </>
    );
};
